/**
 * Prioritization failure-pattern catalog — structural co-occurrences the platform
 * detects (or will detect) before ranking sessions run.
 *
 * Source narrative: demo/PRIORITIZATION_ANTIPATTERNS.md
 * Runtime detection (groups A–B subset): src/lib/poolViolations.ts
 * Live inspector: Relationship Graph → Pool violations panel (GRAPH_POOL_VIOLATIONS)
 */

export type AntipatternGroupId = 'A' | 'B' | 'C' | 'D';

export type AntipatternDisposition = 'block' | 'warn' | 'require_approval' | 'catalog';

export interface AntipatternGroup {
  id: AntipatternGroupId;
  title: string;
  description: string;
}

export interface AntipatternEntry {
  id: string;
  code: string;
  name: string;
  aka: string;
  group: AntipatternGroupId;
  structure: string;
  canon: string;
  pain: string;
  signal: string;
  disposition: AntipatternDisposition;
  status: string;
  /** Matches poolViolations PATTERN_CATALOG when detectable client-side */
  patternId?: string;
}

export const ANTIPATTERN_GROUPS: AntipatternGroup[] = [
  {
    id: 'A',
    title: 'Relationship co-occurrence patterns',
    description:
      'Named combinations of relationship types and scope membership that invalidate or distort ranking.',
  },
  {
    id: 'B',
    title: 'Lineage / identity patterns',
    description: 'Comparability and provenance failures — wrong altitude, orphaned successors, frame drift.',
  },
  {
    id: 'C',
    title: 'Planning-handoff patterns',
    description:
      'Rank ≠ sequence at the prioritization → planning boundary. Detected on the planning side; catalogued here for completeness.',
  },
  {
    id: 'D',
    title: 'Process-integrity patterns',
    description: 'Governance and readiness failures — politics, rigged criteria, zombie carry-forward, unshaped contenders.',
  },
];

export const PRIORITIZATION_ANTIPATTERNS: AntipatternEntry[] = [
  {
    id: 'A1',
    code: 'A1',
    name: 'Two Tickets, One Bet',
    aka: 'the Phantom Duplicate',
    group: 'A',
    structure: 'An unresolved Cluster (duplicate) with both members in the comparison scope.',
    canon: '07 §5.1, Rule 13 (Gate 1 — identity gates everything)',
    pain:
      'The same request entered twice; the ranking double-counts it and whichever copy "wins" is an artifact of duplication. Teams debate two items for weeks, or fund both.',
    signal:
      'Twins flags the duplicate at intake; pool/session creation blocks until identity is resolved (duplicate / variant / same item under another category).',
    disposition: 'block',
    status: '[seeded] Smart Re-engagement Email ↔ Email Nudge v2',
    patternId: 'A1_two_tickets_one_bet',
  },
  {
    id: 'A2',
    code: 'A2',
    name: 'Half a Package',
    aka: 'the Bundle Breaker',
    group: 'A',
    structure: 'A Package (atomic, all-or-nothing) only partially represented in scope.',
    canon: '07 §5.3, Rule 17 (partial package in a scope = flag, settled where the Tracks meet)',
    pain:
      'You commit to part of something that only works whole — the API without the migration, the feature without its kill-switch.',
    signal: 'Scope-activation pass flags: "you have [A] but not [B] from Package [X]."',
    disposition: 'warn',
    status: '[seeded] Onboarding Activation Bundle: Activation present, First-Win missing',
    patternId: 'A2_half_a_package',
  },
  {
    id: 'A3',
    code: 'A3',
    name: 'Both Options Won',
    aka: 'the Either/Or Smuggled as And',
    group: 'A',
    structure: 'An Elaboration parent with two+ option children, both present, none selected.',
    canon: '07 §5.6, Rule 16 (a child must be selected before commitment)',
    pain:
      'Two competing approaches to one need both survive because nobody forced the choice — you fund the ideal and the cheap version of the same thing.',
    signal: '"Select an option" required before commitment; ranking both warns (one bet double-represented).',
    disposition: 'warn',
    status: '[seeded] Activation Checkpoint ideal vs Lite, solution_spectrum',
    patternId: 'A3_both_options_won',
  },
  {
    id: 'A4',
    code: 'A4',
    name: 'Ranked Above Its Own Blocker',
    aka: 'the Hidden Prerequisite',
    group: 'A',
    structure:
      'A hard Dependency where the dependent is in scope but its prerequisite is not (or the dependent out-ranks its prerequisite).',
    canon: '07 §5.4, Rule 19 (hard dep outside scope: warn at inclusion, block at commitment)',
    pain:
      "The #1 priority can't actually start because the thing it needs was never prioritized; the ranking implies an impossible order.",
    signal: 'Dependency caps where the dependent can legitimately sit; flags the missing prerequisite by name.',
    disposition: 'warn',
    status: 'Structural support (Enterprise Tier → SSO → Auth Refactor hard-dep chain)',
    patternId: 'A4_ranked_above_blocker',
  },
  {
    id: 'A5',
    code: 'A5',
    name: 'Counting the Whole and the Parts',
    aka: 'the Aggregation Mirage',
    group: 'A',
    structure: 'An Aggregation/Breakdown parent ranked in the same scope as its children.',
    canon: '07 §5.5/5.5A, Rule 14 (composition children\'s dependencies surface at parent commitment)',
    pain:
      'The roadmap looks twice as full as it is; capacity and credit are counted at both altitudes.',
    signal: 'Composition-aware scope pass surfaces parent+children co-membership.',
    disposition: 'warn',
    status: 'Structural support (Visibility split lineage)',
    patternId: 'A5_whole_and_parts',
  },
  {
    id: 'B1',
    code: 'B1',
    name: 'Comparing Unlike Things',
    aka: 'Altitude Salad · Apples-to-Initiatives',
    group: 'B',
    structure:
      'Comparison Cell mismatch — items of different (category × altitude) in one comparison (e.g. an Opportunity ranked against the Solutions it spawned).',
    canon: '02 §8 (comparability is surfaced, not enforced — owner ruling 2026-09-04), 07 §8',
    pain:
      '"We ranked a strategy against a button." The ranking is meaningless but looks authoritative.',
    signal: 'Surfaced as a warning on the set; never stops a session starting or proceeding.',
    disposition: 'warn',
    status: '[seeded] "Reduce first-week drop-off" L3 opportunity vs L2 solutions',
    patternId: 'B1_altitude_salad',
  },
  {
    id: 'B2',
    code: 'B2',
    name: 'Lineage Amnesia',
    aka: 'the Orphaned Heir',
    group: 'B',
    structure:
      'A split/fork where the parent and successor are both active and both land in scope, or relationships didn\'t transfer to the successor.',
    canon: '07 §5.7, 07B (split retires source; fork keeps it active; disposition records)',
    pain:
      'The old item keeps getting worked while the "real" successor sits idle; or parent and successor both get ranked and double-count.',
    signal: 'Lineage propagation dispositions + parent/successor co-membership warning.',
    disposition: 'warn',
    status: 'Structural support (Activation → Lite fork; both active)',
    patternId: 'B2_lineage_amnesia',
  },
  {
    id: 'B3',
    code: 'B3',
    name: 'The Frame Flip',
    aka: 'Category Drift mid-cycle',
    group: 'B',
    structure:
      'A multi-framed item whose primary frame changes, shifting its Track Cell and breaking package/comparison compatibility.',
    canon: '07 §2.2 (reframe is item-native), Rule 21 (frame change → track compatibility check)',
    pain:
      'An item silently changed comparison classes and invalidated a ranking nobody re-checked.',
    signal: 'Frame-change triggers a comparability recheck against current scope.',
    disposition: 'warn',
    status: 'Structural support (Activation carries 3 frames)',
  },
  {
    id: 'C1',
    code: 'C1',
    name: 'The Plan That Ignored the Ranking',
    aka: 'Silent Rank–Sequence Divergence',
    group: 'C',
    structure:
      'A lower-ranked initiative executes earlier than a higher-ranked one, with no recorded reason.',
    canon: '10 §6, build-contract §4.4 (PlanDivergence artifact required)',
    pain: '"Why are we building the #5 thing before the #1 thing?" — and no one decided that.',
    signal: 'Divergence artifact + explicit tradeoff required before POR authorization.',
    disposition: 'require_approval',
    status: 'Catalog (planning domain)',
  },
  {
    id: 'C2',
    code: 'C2',
    name: 'The Silent Replan',
    aka: 'Priorities Drifted Into a Plan',
    group: 'C',
    structure: 'Constraint pressure quietly rewrites importance (planning mutates priority order).',
    canon: '10 §7, build-contract §4.6 (ReprioritizationTrigger is a governance act, not an inference)',
    pain:
      'The ranking everyone agreed to no longer matches what\'s being built, and the change was never a decision.',
    signal: 'Return-to-prioritization is an explicit, audited governance gate — not a silent side effect.',
    disposition: 'require_approval',
    status: 'Catalog (planning domain)',
  },
  {
    id: 'C3',
    code: 'C3',
    name: 'Capacity Fiction',
    aka: "Everything's P0",
    group: 'C',
    structure: 'The committed set exceeds the capacity bucket; the cut line is theater.',
    canon: '10 §5, build-contract §4.3 (bucket materialization)',
    pain:
      '18 months of work committed to a quarter; the "priorities" can\'t all be done and nobody flagged it.',
    signal: 'Capacity-bucket materialization + over-allocation flag at plan evaluation.',
    disposition: 'warn',
    status: 'Catalog (planning domain)',
  },
  {
    id: 'D1',
    code: 'D1',
    name: 'HiPPO in the Pool',
    aka: 'Loudest-Voice Ranking',
    group: 'D',
    structure:
      'An item enters or is ranked by authority, with the participation model set after the fact.',
    canon: '01 §2A Stage 1 (participation before evaluation), 05 (decision rights + provenance)',
    pain: 'Merit lost to the most senior person in the room; the list reflects politics.',
    signal: 'Participation-stage gate + provenance trail; items show who introduced them and when.',
    disposition: 'warn',
    status: 'Structural support (provenance on every item)',
  },
  {
    id: 'D2',
    code: 'D2',
    name: 'Criteria Gerrymandering',
    aka: 'Criteria Chosen to Pick the Winner',
    group: 'D',
    structure: 'Criteria selected/weighted to favor a predetermined item.',
    canon: '01 §2A Stages 3–4; criteria locked before scoring; drift detection',
    pain: 'Rigorous-looking scoring on rigged inputs; everyone senses it, no one can prove it.',
    signal: 'Criteria locked before scoring; drift-from-baseline surfaced across cycles.',
    disposition: 'warn',
    status: 'Structural support (criteria_finalized_at lock on sessions)',
  },
  {
    id: 'D3',
    code: 'D3',
    name: 'The Zombie Carry-Forward',
    aka: 'the Backlog That Never Dies',
    group: 'D',
    structure: 'Items carried cycle to cycle, never ranked, never killed (drift accumulation).',
    canon: '01 §2B (Drift failure mode), 03 (exit_status: defer vs archive)',
    pain:
      'A graveyard of "someday" items that quietly bloats every cycle and erodes trust in the list.',
    signal: 'Carry-forward aging + drift detection; prompts an explicit keep/kill.',
    disposition: 'warn',
    status: "Structural support (exit_status='deferred')",
  },
  {
    id: 'D4',
    code: 'D4',
    name: 'The Unshaped Contender',
    aka: 'A Sentence Competing With a Spec',
    group: 'D',
    structure: 'A vague, unready item in the same ranking as fully-defined ones.',
    canon: '01 §2A Stage 2, readiness gate (named missing inputs)',
    pain:
      'The loudest one-liner crowds out well-defined work, or the team argues scope mid-session.',
    signal: 'Readiness gate holds it out of the pool with named missing inputs.',
    disposition: 'block',
    status: 'Structural support (flagged vague items in Meridian)',
  },
];

export function antipatternCardClass(isDark: boolean, _group: AntipatternGroupId): string {
  return isDark
    ? ` border border-[var(--rule)] bg-[var(--surface)] p-4 sm:p-5`
    : ` border border-[var(--rule)] bg-[var(--bg-raised)] p-4 sm:p-5`;
}

export function dispositionLabel(disposition: AntipatternDisposition): string {
  switch (disposition) {
    case 'block':
      return 'Block';
    case 'warn':
      return 'Warn';
    case 'require_approval':
      return 'Require approval';
    case 'catalog':
      return 'Catalog';
  }
}

export function dispositionBadgeClass(disposition: AntipatternDisposition, isDark: boolean): string {
  const base = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide';
  switch (disposition) {
    case 'block':
      return `${base} ${isDark ? 'bg-[var(--coral)]/50 text-[var(--coral)] border border-[var(--coral)]/50' : 'bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]'}`;
    case 'warn':
      return `${base} ${isDark ? 'bg-[var(--mustard)]/40 text-[var(--mustard)] border border-[var(--mustard)]/50' : 'bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]'}`;
    case 'require_approval':
      return `${base} ${isDark ? 'bg-[var(--slate)]/40 text-[var(--text-ter)] border border-[var(--slate)]/50' : 'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]'}`;
    case 'catalog':
      return `${base} ${isDark ? 'bg-[var(--bg-raised)] text-[var(--text-ter)] border border-[var(--rule)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)] border border-[var(--rule)]'}`;
  }
}

export function getAntipatternsByGroup(group: AntipatternGroupId): AntipatternEntry[] {
  return PRIORITIZATION_ANTIPATTERNS.filter((e) => e.group === group);
}

export function getAntipatternGroup(id: AntipatternGroupId): AntipatternGroup | undefined {
  return ANTIPATTERN_GROUPS.find((g) => g.id === id);
}
